import { bunja } from 'bunja';
import { number, object, string } from 'yup';

import { i18nBunja } from '@flipster/i18n';
import { ServerError } from '@flipster/utils/constants/server-error';
import { isNullable } from '@flipster/utils/nullable';
import Num, { numberFormatter } from '@flipster/utils/num';
import { V2OrderSideType } from '@flipster/utils/types/base';

const DefaultUnknownErrorMessage = ServerError['api.server.error'];

// 1. TP/SL Price Validation
// - priceHigherThanReference: "The price should be higher than the {priceType} price"
// - priceLowerThanReference: "The price should be lower than the {priceType} price"

// 2. Liquidation Price Validation
// - enterPriceAboveLiquidation: "Enter price above liquidation price: {liqPrice}"
// - enterPriceBelowLiquidation: "Enter price below liquidation price: {liqPrice}"

export type TpslSchema = {
  side: 'Long' | 'Short';
  positionType: 'open' | 'pending';
  price: string;
  basePrice?: string;
  entryPrice: string;
  tpSlType: 'tp' | 'sl';
  triggerPrice?: string;
  leverage: number;
  liqPriceLong?: string;
  liqPriceShort?: string;
};

interface CheckTpSlFormValue {
  side: V2OrderSideType;
  priceType: 'market' | 'trigger' | 'order' | 'entry';
  price?: string;
  entryPrice?: string;
  liqPriceLong?: string;
  liqPriceShort?: string;
}

// Tpsl Time Trigger Validation Error Messages

// 1. Time Trigger Validation
// - triggerRatioExceedsLiquidation: "This will exceed your liquidation amount. Enter a smaller trigger %."
// - tpExceedsHundredPercent: "You can't set a Take Profit that exceeds 100% limit, please set to a smaller amount."

export type TpslTimeTriggerSchema = {
  side: 'Long' | 'Short';
  positionType: 'open' | 'pending';
  tpSlType: 'tp' | 'sl';
  triggerPriceRatio?: string;
  leverage: number;
  maintMarginRate: string;
};

interface CheckTpSlTriggerTimeFormValue {
  side: V2OrderSideType;
  maintMarginRate: string;
  leverage: number;
}

export const validationBunja = bunja(() => {
  const { getT } = bunja.use(i18nBunja);
  const transTrade = getT('Trade.r250401');
  const checkTpSlValidation = (
    valueType: 'tp' | 'sl',
    triggerPrice: string | undefined,
    formValue: CheckTpSlFormValue,
  ) => {
    const { side, priceType, price, liqPriceLong, liqPriceShort } = formValue;
    if (!triggerPrice) return;

    const errorMessage = {
      market: {
        priceHigherRequired: transTrade('Price_higher_market_required_error'),
        priceLowerRequired: transTrade('Price_lower_market_required_error'),
      },
      trigger: {
        priceHigherRequired: transTrade('Price_higher_trigger_required_error'),
        priceLowerRequired: transTrade('Price_lower_trigger_required_error'),
      },
      order: {
        priceHigherRequired: transTrade('Price_higher_order_required_error'),
        priceLowerRequired: transTrade('Price_lower_order_required_error'),
      },
      entry: {
        priceHigherRequired: transTrade('Price_higher_entry_required_error'),
        priceLowerRequired: transTrade('Price_lower_entry_required_error'),
      },
    }[priceType];
    if (valueType === 'tp') {
      if (side === 'Long' && Num(triggerPrice).isLessThanOrEqualTo(price || '0')) {
        throw new Error(errorMessage.priceHigherRequired);
      }
      if (side === 'Short' && Num(triggerPrice).isGreaterThanOrEqualTo(price || '0')) {
        throw new Error(errorMessage.priceLowerRequired);
      }
    }
    if (valueType === 'sl') {
      if (side === 'Long' && Num(triggerPrice).isGreaterThanOrEqualTo(price || '0')) {
        throw new Error(errorMessage.priceLowerRequired);
      }
      if (side === 'Short' && Num(triggerPrice).isLessThanOrEqualTo(price || '0')) {
        throw new Error(errorMessage.priceHigherRequired);
      }
    }

    if ((side === 'Long' && isNullable(liqPriceLong)) || (side === 'Short' && isNullable(liqPriceShort)))
      throw new Error(DefaultUnknownErrorMessage);
    if (side === 'Long' && Num(triggerPrice).isLessThan(liqPriceLong!))
      throw new Error(transTrade('Price_above_liquidation_error', { liquidationPrice: numberFormatter(liqPriceLong) }));
    if (side === 'Short' && Num(triggerPrice).isGreaterThan(liqPriceShort!))
      throw new Error(
        transTrade('Price_below_liquidation_error', { liquidationPrice: numberFormatter(liqPriceShort) }),
      );
  };

  const tpslValidator = object({
    side: string().oneOf(['Long', 'Short']).required(),
    positionType: string().oneOf(['open', 'pending']).required(),
    price: string().required(),
    basePrice: string(),
    entryPrice: string().required(),
    tpSlType: string().oneOf(['tp', 'sl']).required(),
    triggerPrice: string()
      .test({
        name: 'is-trigger-price',
        test(value, ctx) {
          try {
            const { side, positionType, price, entryPrice, liqPriceLong, liqPriceShort, tpSlType } =
              ctx.parent as TpslSchema;
            const isOpenPosition = positionType === 'open';
            if (!value || !price) return true;
            checkTpSlValidation(tpSlType, value, {
              side,
              priceType: isOpenPosition ? 'market' : 'order',
              price,
              entryPrice,
              liqPriceLong,
              liqPriceShort,
            });
            return true;
          } catch (err) {
            return ctx.createError({
              type: 'custom',
              message: (err as Error).message,
            });
          }
        },
      })
      .optional(),
    leverage: number().required(),
    liqPriceLong: string().optional(),
    liqPriceShort: string().optional(),
  });

  const checkTpSlTriggerTimeValidation = (
    valueType: 'tp' | 'sl',
    triggerPriceRatio: string | undefined,
    formValue: CheckTpSlTriggerTimeFormValue,
  ) => {
    const { maintMarginRate, side, leverage } = formValue;
    if (!triggerPriceRatio) return;
    if (
      valueType === 'sl' &&
      Num(triggerPriceRatio).dividedBy(100).plus(maintMarginRate).multipliedBy(leverage).isGreaterThan(1)
    ) {
      throw new Error(transTrade('Tpsl_exceed_liquidation_error'));
    }
    if (side === 'Short' && valueType === 'tp' && Num(triggerPriceRatio).isGreaterThanOrEqualTo(100))
      throw new Error(transTrade('Take_profit_over_limit_error'));
  };

  const tpslTimeTriggerValidator = object({
    side: string().oneOf(['Long', 'Short']).required(),
    positionType: string().oneOf(['open', 'pending']).required(),
    tpSlType: string().oneOf(['tp', 'sl']).required(),
    triggerPriceRatio: string()
      .test({
        name: 'is-trigger-price-ratio',
        test(value, ctx) {
          if (!value) return true;
          try {
            const { maintMarginRate, side, tpSlType, leverage } = ctx.parent as TpslTimeTriggerSchema;
            checkTpSlTriggerTimeValidation(tpSlType, value, {
              side,
              maintMarginRate,
              leverage,
            });
            return true;
          } catch (err) {
            return ctx.createError({
              type: 'custom',
              message: (err as Error).message,
            });
          }
        },
      })
      .optional(),
    leverage: number().required(),
    maintMarginRate: string().required(),
  });

  return {
    tpslValidator,
    tpslTimeTriggerValidator,
  };
});
validationBunja.debugLabel = 'validationBunja';
